import { routeName } from '~/@common/constants'
import {
  HANDWRITTEN_NOTE_DB_NAME,
  STUDENT_WORKSHEET_NOTE_KEY_PATH,
  STUDENT_WORKSHEET_NOTE_STORE,
} from '~/@common/services/indexedDb.service'

import { USER_STORAGE_KEYS } from '../services/storage.service'
import { INTEGRATION_KEY, INTEGRATION_MESSAGE_TYPE, INTEGRATION_VALUE } from './constants'
import { getDomainPair, isSenderSide } from './integrationDomain'
import {
  type IndexedDBNoteEntry,
  INTEGRATION_IFRAME_MESSAGE,
  type IntegrationMessage,
} from './messageProtocol'
import { isIntegrationBridgePath } from './routeGuards'
import { IS_INTERNAL_INTEGRATION_KEY } from './storage'

type SyncSuccessPayload = {
  localStorage: Record<string, string>
  sessionStorage: Record<string, string>
  indexedDBNotes: IndexedDBNoteEntry[]
}

// receiver (통합 student) iframe sync 흐름의 caller 응답 — initialize.ts 분기 결정용.
//   - continue   : 일반 init 진행 (SYNC_EMPTY / TIMEOUT)
//   - reloaded   : location.reload 호출됨 → caller 즉시 return (SYNC_SUCCESS 직후)
export type ReceiverHandlerResult = { action: 'continue' } | { action: 'reloaded' }

// "이번 mount 에서 한 번 다녀옴" marker — 다음 mount 부터 shouldSkipIntegration 로 skip.
//   - SYNC_SUCCESS / SYNC_EMPTY 진입 시 호출 (정상 종료 = 다음에 또 시도할 필요 없음).
//   - TIMEOUT 으로 끝난 케이스는 marker 안 박음 (sender 미배포 가능성 → 재시도 여지 보존).
const markMigrationDone = (): void => {
  localStorage.setItem(INTEGRATION_KEY.MIGRATION_DONE, INTEGRATION_VALUE.TRUE)
}

// ★ IndexedDB 필기 노트 write — sender 우선 덮어쓰기 (receiver = 베이직 student.com 시절 IndexedDB 미사용 → 충돌 가능성 낮음).
const writeHandwrittenNotes = async (notes: IndexedDBNoteEntry[]): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(HANDWRITTEN_NOTE_DB_NAME, 1)
      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STUDENT_WORKSHEET_NOTE_STORE)) {
          db.createObjectStore(STUDENT_WORKSHEET_NOTE_STORE, {
            keyPath: [...STUDENT_WORKSHEET_NOTE_KEY_PATH],
          })
        }
      }
      req.onsuccess = () => {
        const db = req.result
        try {
          const tx = db.transaction(STUDENT_WORKSHEET_NOTE_STORE, 'readwrite')
          const store = tx.objectStore(STUDENT_WORKSHEET_NOTE_STORE)
          notes.forEach((note) => store.put(note))
          tx.oncomplete = () => {
            db.close()
            resolve()
          }
          tx.onerror = () => {
            db.close()
            resolve()
          }
        } catch {
          db.close()
          resolve()
        }
      }
      req.onerror = () => resolve()
      req.onblocked = () => resolve()
    } catch {
      resolve()
    }
  })
}

// postMessage event.data 가 IntegrationMessage 모양인지 확인하는 narrowing predicate.
//   strict shape 검증은 안 함 — type 필드만 보고 handleWireMessage 의 switch 가 자체 필터.
const isIntegrationMessage = (data: unknown): data is IntegrationMessage =>
  typeof data === 'object' && data !== null && 'type' in data

const SYNC_TIMEOUT_MS = 3000
const RELOAD_DELAY_MS = 250 // SYNC_COMPLETED dispatch 보장 후 reload
// jwtTokenService 가 사용하는 토큰 키 — LS / SS 양쪽 모두에 저장 가능 (로그인 유지 ✓ → LS, ✗ → SS).
const TOKEN_KEYS = ['accessToken', 'refreshToken', 'x-auth-token'] as const
// 세션 키로 묶는 사용자 식별자 — beforeLoginId 는 단순 UI 힌트라 제외.
const USER_IDENTITY_LS_KEYS = [
  USER_STORAGE_KEYS.studentId,
  USER_STORAGE_KEYS.parentId,
  USER_STORAGE_KEYS.academyId,
  USER_STORAGE_KEYS.isInitialPassword,
  USER_STORAGE_KEYS.isPasswordChangedRequired,
] as const

const shouldSkipIntegration = (): boolean => !!localStorage.getItem(INTEGRATION_KEY.MIGRATION_DONE)

const hasTokenInPayload = (payload: SyncSuccessPayload): boolean =>
  TOKEN_KEYS.some(
    (key) => payload.localStorage[key] !== undefined || payload.sessionStorage[key] !== undefined,
  )

// 송신 payload 의 사용자 식별자 — studentId 또는 parentId 둘 중 하나라도 있으면 "유의미한 식별자" 로 인정.
//   academyId 등 부수 키만 있는 케이스는 식별자 있음으로 치지 않음 (의미 없는 잔존물).
const hasIdentityInPayload = (payload: SyncSuccessPayload): boolean =>
  payload.localStorage[USER_STORAGE_KEYS.studentId] !== undefined ||
  payload.localStorage[USER_STORAGE_KEYS.parentId] !== undefined

// parentId는 로컬스토리지에만 있음
const hasParentId = (): boolean => !!localStorage.getItem(USER_STORAGE_KEYS.parentId)

// 기타 데이터 merge — 송신 payload 의 비-세션 키만 자기 storage 에 setItem.
//   - 자기 unique 기타 데이터 (학습 데이터, UI 상태, 디바이스 정보) 보존
//   - 같은 키는 송신값으로 덮어쓰기
//   - 토큰 / 식별자 (세션 키) 는 별도 정책 — 여기서 건드리지 ✗
//   - INTEGRATION_KEY_V1_* internal 키 침투 차단
const mergeMiscData = (target: Storage, payload: Record<string, string>): void => {
  const SESSION_KEYS: readonly string[] = [...TOKEN_KEYS, ...USER_IDENTITY_LS_KEYS]
  Object.entries(payload).forEach(([key, value]) => {
    if (IS_INTERNAL_INTEGRATION_KEY(key)) return
    if (SESSION_KEYS.includes(key)) return
    target.setItem(key, value)
  })
}

// 세션 키 전체 교체 — 송신 완전 세션 진입 시에만 호출.
//   - LS / SS 양쪽에서 모든 세션 키 제거 후 송신 payload 가 둔 위치에 그대로 적용.
//   - 이렇게 안 하면 수신 LS-mode 토큰 + 송신 SS-mode 토큰 동시 잔존 → stale 토큰으로 인한 인증 깨짐.
const replaceSessionKeys = (payload: SyncSuccessPayload): void => {
  TOKEN_KEYS.forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
    const lsValue = payload.localStorage[key]
    const ssValue = payload.sessionStorage[key]
    if (lsValue !== undefined) localStorage.setItem(key, lsValue)
    if (ssValue !== undefined) sessionStorage.setItem(key, ssValue)
  })
  USER_IDENTITY_LS_KEYS.forEach((key) => {
    localStorage.removeItem(key)
    const lsValue = payload.localStorage[key]
    if (lsValue !== undefined) localStorage.setItem(key, lsValue)
  })
}

// ★ sender payload 를 자기 storage 에 적용.
//   ⚠ marker 는 여기서 박지 않음 — 호출자가 *뒤에* markMigrationDone 호출.
//
// 정책 — 두 축:
//   [A] 세션 키 (토큰 + 식별자):
//     - 송신 완전 세션 (T+ID) → LS/SS 양쪽 wipe 후 송신값 적용 (LS/SS 모드 그대로 이관, stale 잔존 ✗)
//     - 송신 불완전 (T만 / ID만 / 기타 데이터만) → 자기 세션 키 손대지 ✗
//   [B] 기타 데이터 (세션 키 외 모든 키):
//     - 항상 송신 payload merge (같은 키는 송신 우선, 자기 unique 키는 보존)
//     - 학습 데이터/UI 상태 등 수신측 기타 데이터 wipe ✗
const applySenderStoragePayload = (payload: SyncSuccessPayload): void => {
  const senderHasToken = hasTokenInPayload(payload)
  const senderHasIdentity = hasIdentityInPayload(payload)
  const senderHasCompleteSession = senderHasToken && senderHasIdentity

  if (senderHasCompleteSession) {
    // 세션 키 전체 교체 + 기타 데이터 merge
    replaceSessionKeys(payload)
  }
  // 기타 데이터 merge — 두 분기 공통
  mergeMiscData(localStorage, payload.localStorage)
  mergeMiscData(sessionStorage, payload.sessionStorage)
}

const createHiddenIframe = (title: string): HTMLIFrameElement => {
  const iframe = document.createElement('iframe')
  iframe.title = title
  iframe.setAttribute('aria-hidden', 'true')
  iframe.setAttribute('tabindex', '-1')
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;'
  return iframe
}

// ★ receiver 측 sender-iframe 동기 흐름 — 선형 시퀀스 + single-shot 가드.
//
//   ① iframe 띄우고 3 초 timeout 보험.
//   ② iframe.load → REQUEST_SYNC 송신.
//   ③ sender 응답 분기:
//      ├─ SYNC_SUCCESS → applyStorage → IDB write (있으면) → SYNC_COMPLETED → 250ms → reload
//      ├─ SYNC_EMPTY  → MIGRATION_DONE marker → continue
//      └─ TIMEOUT     → 그냥 종료 (marker ✗ — 재시도 여지)
const requestSenderStorageSync = (senderOrigin: string): Promise<ReceiverHandlerResult> =>
  new Promise((resolve) => {
    const iframe = createHiddenIframe('integration-sync')

    const stopListening = (): void => {
      window.removeEventListener('message', handleSenderMessage)
      clearTimeout(timeoutId)
    }

    // 단일 종료 지점 — stopListening + iframe 제거 + promise resolve.
    //   iframe.remove / Promise.resolve 모두 idempotent — 중복 호출 시 첫 resolve 후 두 번째는 무시.
    const finish = (result: ReceiverHandlerResult): void => {
      stopListening()
      iframe.remove()
      resolve(result)
    }

    // SYNC_SUCCESS 처리 — storage 동기 적용 → IDB await → marker → SYNC_COMPLETED → 250ms → reload.
    //   IDB 끝난 *뒤* 에 marker 박아야 IDB 실패 시 marker 만 남는 부분 마이그 상태 회피.
    const handleSyncSuccess = async (payload: SyncSuccessPayload): Promise<void> => {
      applySenderStoragePayload(payload)
      if (payload.indexedDBNotes.length > 0) {
        await writeHandwrittenNotes(payload.indexedDBNotes)
      }
      markMigrationDone()
      // receiver parent ──SYNC_COMPLETED──▶ sender iframe (sender 가 자기 marker 저장)
      iframe.contentWindow?.postMessage(INTEGRATION_IFRAME_MESSAGE.SYNC_COMPLETED, senderOrigin)
      // 250ms = SYNC_COMPLETED dispatch 보장
      setTimeout(() => {
        window.location.reload()
        finish({ action: 'reloaded' })
      }, RELOAD_DELAY_MS)
    }

    const handleSenderMessage = (event: MessageEvent) => {
      if (event.origin !== senderOrigin) return
      if (!isIntegrationMessage(event.data)) return

      if (event.data.type === INTEGRATION_MESSAGE_TYPE.SYNC_EMPTY) {
        markMigrationDone()
        finish({ action: 'continue' })
        return
      }
      if (event.data.type === INTEGRATION_MESSAGE_TYPE.SYNC_SUCCESS) {
        stopListening()
        // fire-and-forget — handleSyncSuccess 내부에서 IDB await → marker → SYNC_COMPLETED → reload 순서로 진행.
        void handleSyncSuccess({
          localStorage: event.data.localStorage,
          sessionStorage: event.data.sessionStorage,
          indexedDBNotes: event.data.indexedDBNotes ?? [],
        })
      }
    }

    window.addEventListener('message', handleSenderMessage)

    const timeoutId = setTimeout(() => {
      finish({ action: 'continue' })
    }, SYNC_TIMEOUT_MS)
    iframe.addEventListener('load', () => {
      iframe.contentWindow?.postMessage(INTEGRATION_IFRAME_MESSAGE.REQUEST_SYNC, senderOrigin)
    })
    iframe.src = `${senderOrigin}${routeName.integration.sync}?from=${encodeURIComponent(window.location.origin)}`
    document.body.appendChild(iframe)
  })

// ★ student.{root} (receiver) 측 핸들러
//
//   흐름: invisible iframe 으로 sender (student-up.{root}/integration/sync?from=<자기 origin>)
//   띄움 → iframe.onload 후 REQUEST_SYNC 발신 → sender 가 marker 비교 후 응답:
//     - SYNC_SUCCESS  : replaceStorage + MIGRATION_DONE marker + SYNC_COMPLETED + 250ms 후 reload
//     - SYNC_EMPTY    : MIGRATION_DONE marker 만 박고 skip (다녀온 것 = 1회성 끝)
//     - timeout (3s)  : marker 안 박음 — sender 옛 코드/부재 가능성, 재시도 여지 남김
export const handleStudentMainLogic = (): Promise<ReceiverHandlerResult> => {
  if (window.self !== window.top) return Promise.resolve({ action: 'continue' })

  if (isIntegrationBridgePath(window.location.pathname)) {
    return Promise.resolve({ action: 'continue' })
  }
  // /integration/* iframe target 전용 path —
  //   top-level 직접 진입 시에도 핸들러 동작 ✗ (handleStudentUpLogic 과 symmetric).
  if (window.location.pathname === routeName.integration.sync) {
    return Promise.resolve({ action: 'continue' })
  }
  if (window.location.pathname === routeName.integration.logoutClear) {
    return Promise.resolve({ action: 'continue' })
  }
  if (window.location.pathname === routeName.integration.probe) {
    return Promise.resolve({ action: 'continue' })
  }
  if (window.location.pathname === routeName.integration.devReset) {
    return Promise.resolve({ action: 'continue' })
  }
  if (shouldSkipIntegration()) return Promise.resolve({ action: 'continue' })

  // ★ parentId 가 있으면 iframe 자체 띄우지 않음 (학부모는 베이직 student 에만 존재했었음 → iframe 불요).
  if (hasParentId()) return Promise.resolve({ action: 'continue' })

  // 자기가 sender(통합 student-up) 측이면 skip — handleStudentUpLogic 이 처리.
  if (isSenderSide()) return Promise.resolve({ action: 'continue' })

  const pair = getDomainPair()
  if (!pair) return Promise.resolve({ action: 'continue' })

  return requestSenderStorageSync(pair.iframeOrigin)
}
